import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  PlayIcon, 
  StopIcon, 
  ArrowDownTrayIcon, 
  BeakerIcon,
  DocumentTextIcon,
  CubeIcon,
  TableCellsIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/solid';
import CustomMidiRoll from './CustomMidiRoll';
import midiService, { MidiService } from '../services/midiService';
import apiClient from '../utils/apiClient';

const AdminPanel = () => {
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [completedNotes, setCompletedNotes] = useState([]); // Notes that have ended (note_off received)
  const [activeNotes, setActiveNotes] = useState([]); // Notes currently being held
  const [fileName, setFileName] = useState('');
  const sessionStartTime = useRef(null);
  const lastNoteStartTime = useRef(null);
  
  // Training state
  const [isTraining, setIsTraining] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [trainingResult, setTrainingResult] = useState(null);
  const [csvFiles, setCsvFiles] = useState([]);
  const [selectedCsvFiles, setSelectedCsvFiles] = useState(new Set());
  const [modelConfig, setModelConfig] = useState({
    hidden_layers: [128, 64, 32],
    epochs: 100,
    learning_rate: 0.001,
    batch_size: 32,
    use_dropout: false,
    dropout_rate: 0.2,
    num_classes: 2,  // 2 for binary (left/right), 3 for multi-class (left/right/single-hand)
    validation_split: 0.2,
    sequence_length: 16  // Number of notes in each training sequence
  });
  
  // Exercise and embedding state
  const [exercises, setExercises] = useState([]);
  const [embeddings, setEmbeddings] = useState([]);
  const [models, setModels] = useState([]);
  const [activeModel, setActiveModel] = useState(null);
  const [recognitionThresholds, setRecognitionThresholds] = useState({
    min_similarity: 0.93,
    min_margin: 0.005,
    min_pitch_agreement: 0.8,
    min_notes: 10,
    require_hand_agreement: true
  });
  const [recognitionSettingsStatus, setRecognitionSettingsStatus] = useState('');
  const [savingRecognitionSettings, setSavingRecognitionSettings] = useState(false);
  
  // UI state
  const [activeTab, setActiveTab] = useState('record');
  const [adminLoadError, setAdminLoadError] = useState('');
  
  // Analysis state for record mode
  const [recordAnalysis, setRecordAnalysis] = useState(null);
  
  // Model configuration
  const [loadedModelConfig, setLoadedModelConfig] = useState({ sequence_length: 16 });

  const loadAdminJson = async (url, label) => {
    const response = await apiClient.fetch(url, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let detail = '';
      try {
        const body = await response.json();
        detail = body.detail || body.message || '';
      } catch (error) {
        // The label and response status still provide an honest failure state.
      }
      throw new Error(
        `${label} could not be loaded (${response.status})${detail ? `: ${detail}` : ''}`
      );
    }

    return response.json();
  };

  // Initialize MIDI
  useEffect(() => {
    const initMIDI = async () => {
      try {
        if (MidiService.isSupported()) {
          await midiService.initialize();
          console.log('AdminPanel: MIDI service initialized');
        } else {
          console.error('AdminPanel: Web MIDI API not supported');
        }
      } catch (error) {
        console.error('AdminPanel: Failed to initialize MIDI:', error);
      }
    };
    
    initMIDI();
    
    return () => {
      // Cleanup MIDI
      if (window._adminMidiListener) {
        midiService.removeEventListener(window._adminMidiListener);
        window._adminMidiListener = null;
      }
    };
  }, []);

  // Classification function - similar to practice mode but with manual override capability
  const classifyNotes = useCallback(async (allNotes, noteIndexToClassify = null) => {
    console.log(`AdminPanel: Classifying ${allNotes.length} notes`);
    
    try {
      // Calculate relative timing
      const notesWithRelativeTiming = allNotes.map((note, index) => {
        let timeSinceLastNote = 0;
        if (index > 0) {
          timeSinceLastNote = note.timestamp - allNotes[index - 1].timestamp;
        }
        
        return {
          note: note.note,
          velocity: note.velocity,
          timestamp: note.timestamp,
          duration: note.duration,
          channel: note.channel || 0,
          time_since_last: timeSinceLastNote,
          hand: note.hand || null,
          finger: null,
          event_type: 'note_on'
        };
      });
      
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/classify-notes`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: notesWithRelativeTiming
        })
      });
      
      if (response.ok) {
        const classifiedData = await response.json();
        console.log(`AdminPanel: Received ${classifiedData.notes?.length || 0} classified notes`);
        
        setCompletedNotes(prev => {
          const updated = [...prev];
          
          classifiedData.notes.forEach((classifiedNote, index) => {
            if (index < updated.length && !updated[index].manuallySet) {
              // Only update if not manually set
              updated[index] = {
                ...updated[index],
                hand: classifiedNote.hand,
                confidence: classifiedNote.confidence
              };
            }
          });
          
          return updated;
        });
        
        // Also update active notes
        setActiveNotes(prev => {
          return prev.map(activeNote => {
            const matchingClassified = classifiedData.notes.find((classified, idx) => {
              if (idx < allNotes.length) {
                const note = allNotes[idx];
                return note.note === activeNote.note && 
                       Math.abs(note.timestamp - activeNote.timestamp) < 0.1;
              }
              return false;
            });
            
            if (matchingClassified && !activeNote.manuallySet) {
              return {
                ...activeNote,
                hand: matchingClassified.hand,
                confidence: matchingClassified.confidence
              };
            }
            return activeNote;
          });
        });
        
        return classifiedData;
      }
    } catch (error) {
      console.error('AdminPanel: Classification error:', error);
    }
    
    return null;
  }, []);

  // Fetch model configuration
  const fetchModelConfig = async () => {
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/model-config?t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        cache: 'no-cache'  // Force fresh fetch
      });
      
      if (response.ok) {
        const config = await response.json();
        console.log('AdminPanel: Model config loaded:', config);
        setLoadedModelConfig(config);
      } else {
        console.error('AdminPanel: Failed to fetch model config, status:', response.status);
      }
    } catch (error) {
      console.error('AdminPanel: Failed to fetch model config:', error);
    }
  };

  const fetchRecognitionThresholds = async () => {
    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/recognition-thresholds`,
        {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (!response.ok) {
        throw new Error('Could not load recognition thresholds');
      }
      setRecognitionThresholds(await response.json());
    } catch (error) {
      console.error('Failed to fetch recognition thresholds:', error);
      setRecognitionSettingsStatus(error.message);
    }
  };

  const saveRecognitionThresholds = async () => {
    setSavingRecognitionSettings(true);
    setRecognitionSettingsStatus('');
    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/recognition-thresholds`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(recognitionThresholds)
        }
      );
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.detail || 'Could not save recognition thresholds');
      }
      setRecognitionThresholds(body);
      setRecognitionSettingsStatus('Recognition thresholds saved.');
    } catch (error) {
      console.error('Failed to save recognition thresholds:', error);
      setRecognitionSettingsStatus(error.message);
    } finally {
      setSavingRecognitionSettings(false);
    }
  };

  // Fetch initial data on mount
  useEffect(() => {
    fetchModelConfig();
    fetchTrainingStatus();
    fetchCsvFiles();
    fetchExercises();
    fetchEmbeddings();
    fetchModels();
    fetchRecognitionThresholds();
  }, []);

  const fetchTrainingStatus = async () => {
    try {
      const data = await loadAdminJson(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/training-status`,
        'Training status'
      );
      setTrainingStatus(data);
    } catch (error) {
      console.error('Failed to fetch training status:', error);
      setAdminLoadError(error.message);
    }
  };

  const fetchCsvFiles = async () => {
    try {
      const data = await loadAdminJson(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/csv-files`,
        'Training data'
      );
      setCsvFiles(data.files || []);
    } catch (error) {
      console.error('Failed to fetch CSV files:', error);
      setAdminLoadError(error.message);
    }
  };

  const fetchExercises = async () => {
    try {
      const data = await loadAdminJson(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/exercises`,
        'Exercises'
      );
      setExercises(data.exercises || []);
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
      setAdminLoadError(error.message);
    }
  };

  const fetchEmbeddings = async () => {
    try {
      const data = await loadAdminJson(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/embeddings`,
        'Embeddings'
      );
      setEmbeddings(data.embeddings || []);
    } catch (error) {
      console.error('Failed to fetch embeddings:', error);
      setAdminLoadError(error.message);
    }
  };

  const fetchModels = async () => {
    try {
      const data = await loadAdminJson(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/models`,
        'Models'
      );
      setModels(data.models || []);
      setActiveModel(data.active_model || null);
    } catch (error) {
      console.error('Failed to fetch models:', error);
      setAdminLoadError(error.message);
    }
  };

  const startRecording = async () => {
    try {
      console.log('AdminPanel: Starting recording');
      setCompletedNotes([]);
      setActiveNotes([]);
      setIsRecording(true);
      
      // Set session start time
      sessionStartTime.current = Date.now();
      lastNoteStartTime.current = null;
      
      // Get MIDI devices
      const devices = midiService.getInputDevices();
      if (devices.length === 0) {
        alert('No MIDI devices found. Please connect a MIDI device.');
        setIsRecording(false);
        return;
      }
      
      // Connect to first available device
      const targetDevice = devices[0];
      
      // Remove any existing listener
      if (window._adminMidiListener) {
        midiService.removeEventListener(window._adminMidiListener);
        window._adminMidiListener = null;
      }
      
      // Connect to MIDI device
      midiService.connectToDevice(targetDevice.id);
      midiService.clearNoteBuffer();
      
      console.log('AdminPanel: Connected to MIDI device:', targetDevice.name);
      
      // Add MIDI listener
      const midiListener = (eventType, data) => {
        if (eventType === 'note') {
          if (data.event_type === 'note_on') {
            // Note ON: Add to active notes
            const currentTime = Date.now();
            const sessionTime = (currentTime - sessionStartTime.current) / 1000;
            
            const activeNote = {
              note: data.note,
              velocity: data.velocity,
              timestamp: sessionTime,
              startTime: currentTime,
              channel: data.channel || 0,
              hand: null, // Will be classified
              manuallySet: false
            };
            
            console.log(`AdminPanel: Note ON - Pitch: ${data.note}, Time: ${sessionTime.toFixed(3)}s`);
            
            setActiveNotes(prev => [...prev, activeNote]);
            lastNoteStartTime.current = sessionTime;
            
          } else if (data.event_type === 'note_off') {
            // Note OFF: Move from active to completed
            setActiveNotes(prev => {
              const activeIndex = prev.findIndex(n => n.note === data.note);
              if (activeIndex === -1) return prev;
              
              const activeNote = prev[activeIndex];
              const currentTime = Date.now();
              const duration = currentTime - activeNote.startTime;
              
              const completedNote = {
                ...activeNote,
                duration: duration,
                endTime: currentTime,
                hand: activeNote.hand || null,
                confidence: activeNote.confidence || null,
                manuallySet: activeNote.manuallySet || false
              };
              
              console.log(`AdminPanel: Note completed - Pitch: ${completedNote.note}, Duration: ${duration}ms`);
              
              // Add to completed notes and trigger classification
              setCompletedNotes(prevCompleted => {
                const newCompleted = [...prevCompleted, completedNote];
                
                // Classify notes when we have enough context
                const minNotesNeeded = loadedModelConfig.sequence_length || 16;
                if (newCompleted.length === minNotesNeeded) {
                  console.log(`AdminPanel: Reached ${minNotesNeeded} notes, classifying all`);
                  classifyNotes(newCompleted);
                } else if (newCompleted.length > minNotesNeeded) {
                  console.log(`AdminPanel: Note ${newCompleted.length}, classifying incrementally`);
                  classifyNotes(newCompleted, newCompleted.length - 1);
                }
                
                return newCompleted;
              });
              
              // Remove from active notes
              return prev.filter((_, index) => index !== activeIndex);
            });
          }
        }
      };
      
      midiService.addEventListener(midiListener);
      window._adminMidiListener = midiListener;
      
    } catch (error) {
      console.error('AdminPanel: Failed to start recording:', error);
      alert('Failed to start recording: ' + error.message);
      setIsRecording(false);
    }
  };

  const stopRecording = async () => {
    console.log('AdminPanel: Stopping recording');
    
    // Remove MIDI listener
    if (window._adminMidiListener) {
      midiService.removeEventListener(window._adminMidiListener);
      window._adminMidiListener = null;
    }
    
    // Stop MIDI recording
    midiService.disconnect();
    
    // Final classification of all notes if needed
    if (completedNotes.length > 0) {
      if (completedNotes.length < 16) {
        // Force classification even with fewer than 16 notes
        await classifyNotes(completedNotes);
      }
      // Analyze the recorded data
      await analyzeRecordedData(completedNotes);
    }
    
    setIsRecording(false);
  };

  const getNoteNameFromMIDI = (midiNote) => {
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octave = Math.floor(midiNote / 12) - 1;
    const noteName = noteNames[midiNote % 12];
    return `${noteName}${octave}`;
  };

  const toggleNoteHand = (noteToToggle) => {
    console.log('AdminPanel: Toggling hand for note:', noteToToggle);
    
    setCompletedNotes(prev => {
      return prev.map(note => {
        // Match by timestamp and note number
        if (Math.abs(note.timestamp - noteToToggle.timestamp) < 0.001 && 
            note.note === noteToToggle.note) {
          return {
            ...note,
            hand: note.hand === 'left' ? 'right' : 'left',
            manuallySet: true // Mark as manually set so it won't be overridden
          };
        }
        return note;
      });
    });
  };

  const saveRecording = async () => {
    if (!fileName.trim()) {
      alert('Please enter a file name');
      return;
    }

    if (completedNotes.length === 0) {
      alert('No notes recorded');
      return;
    }

    try {
      // Format notes for saving
      const formattedNotes = completedNotes.map(note => ({
        note: note.note,
        velocity: note.velocity,
        timestamp: note.timestamp,
        duration: note.duration,
        channel: note.channel || 0,
        hand: note.hand || 'left',
        event_type: 'note_on'
      }));
      
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/save-recording`, {
        method: 'POST',
        credentials: 'include',  // Include HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filename: fileName,
          notes: formattedNotes
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Recording saved successfully as ${result.filename}`);
        setCompletedNotes([]);
        setActiveNotes([]);
        setFileName('');
        fetchTrainingStatus();
        fetchCsvFiles();
        fetchEmbeddings();
      } else {
        const error = await response.json();
        alert(`Failed to save recording: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to save recording:', error);
      alert('Failed to save recording');
    }
  };

  const trainModel = async () => {
    setIsTraining(true);
    setTrainingResult(null);

    // Get selected files or use all if none selected
    const filesToUse = selectedCsvFiles.size > 0 
      ? Array.from(selectedCsvFiles)
      : csvFiles.map(f => f.name);

    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/train-model`, {
        method: 'POST',
        credentials: 'include',  // Include HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...modelConfig,
          csv_files: filesToUse
        })
      });

      if (response.ok) {
        const result = await response.json();
        setTrainingResult(result);
        alert(`Model trained successfully! Validation accuracy: ${(result.val_accuracy * 100).toFixed(2)}%`);
        fetchTrainingStatus();
        fetchModels();
      } else {
        const error = await response.json();
        alert(`Failed to train model: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to train model:', error);
      alert('Failed to train model');
    } finally {
      setIsTraining(false);
    }
  };

  const activateModel = async (modelName) => {
    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/activate-model/${modelName}`, {
        method: 'POST',
        credentials: 'include',  // Include HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert(`Model ${modelName} activated successfully`);
        fetchModels();
      } else {
        const error = await response.json();
        alert(`Failed to activate model: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to activate model:', error);
      alert('Failed to activate model');
    }
  };


  const toggleCsvSelection = (fileName) => {
    setSelectedCsvFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      } else {
        newSet.add(fileName);
      }
      return newSet;
    });
  };

  // Analysis function for recorded data
  const analyzeRecordedData = async (notes) => {
    // Calculate comprehensive statistics
    const leftNotes = notes.filter(n => n.hand === 'left');
    const rightNotes = notes.filter(n => n.hand === 'right');
    
    // Calculate intervals PER HAND (not mixed)
    const calculateHandIntervals = (handNotes) => {
      const intervals = [];
      for (let i = 1; i < handNotes.length; i++) {
        intervals.push(handNotes[i].timestamp - handNotes[i - 1].timestamp);
      }
      return intervals;
    };
    
    const leftIntervals = calculateHandIntervals(leftNotes);
    const rightIntervals = calculateHandIntervals(rightNotes);
    
    // Calculate average intervals per hand
    const avgLeftInterval = leftIntervals.length > 0 ? leftIntervals.reduce((a, b) => a + b, 0) / leftIntervals.length : 0;
    const avgRightInterval = rightIntervals.length > 0 ? rightIntervals.reduce((a, b) => a + b, 0) / rightIntervals.length : 0;
    
    // Calculate tempo in BPM (assuming 16th notes, 4 per beat)
    const calculateBPM = (handNotes) => {
      if (handNotes.length < 2) return 0;
      const timeSpan = handNotes[handNotes.length - 1].timestamp - handNotes[0].timestamp;
      if (timeSpan <= 0) return 0;
      const numIntervals = handNotes.length - 1;
      const beats = numIntervals / 4; // 4 sixteenth notes per beat
      const minutes = timeSpan / 60;
      return minutes > 0 ? Math.round(beats / minutes) : 0;
    };
    
    const leftBPM = calculateBPM(leftNotes);
    const rightBPM = calculateBPM(rightNotes);
    const overallBPM = calculateBPM(notes);
    
    // Velocity statistics
    const velocities = notes.map(n => n.velocity);
    const avgVelocity = velocities.reduce((a, b) => a + b, 0) / velocities.length;
    const minVelocity = Math.min(...velocities);
    const maxVelocity = Math.max(...velocities);
    
    // Duration statistics
    const durations = notes.map(n => n.duration || 0);
    const avgDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
    
    // Pitch range
    const pitches = notes.map(n => n.note);
    const minPitch = Math.min(...pitches);
    const maxPitch = Math.max(...pitches);
    
    // Hand-specific stats
    const leftVelocity = leftNotes.length > 0 ? 
      leftNotes.reduce((a, n) => a + n.velocity, 0) / leftNotes.length : 0;
    const rightVelocity = rightNotes.length > 0 ? 
      rightNotes.reduce((a, n) => a + n.velocity, 0) / rightNotes.length : 0;
    
    // Finger-by-finger analysis
    const fingerAnalysis = {
      left: {1: [], 2: [], 3: [], 4: [], 5: []},
      right: {1: [], 2: [], 3: [], 4: [], 5: []}
    };
    
    // Collect finger data
    notes.forEach(note => {
      if (note.finger && note.hand) {
        const hand = note.hand;
        const finger = note.finger;
        if (fingerAnalysis[hand] && fingerAnalysis[hand][finger]) {
          fingerAnalysis[hand][finger].push({
            velocity: note.velocity,
            duration: note.duration || 0,
            timestamp: note.timestamp,
            note: note.note
          });
        }
      }
    });
    
    // Calculate finger statistics
    const fingerStats = {
      left: {},
      right: {}
    };
    
    Object.keys(fingerAnalysis).forEach(hand => {
      Object.keys(fingerAnalysis[hand]).forEach(finger => {
        const fingerNotes = fingerAnalysis[hand][finger];
        if (fingerNotes.length > 0) {
          const velocities = fingerNotes.map(n => n.velocity);
          const durations = fingerNotes.map(n => n.duration);
          
          // Calculate intervals for this finger
          const fingerIntervals = [];
          for (let i = 1; i < fingerNotes.length; i++) {
            fingerIntervals.push(fingerNotes[i].timestamp - fingerNotes[i - 1].timestamp);
          }
          
          fingerStats[hand][finger] = {
            count: fingerNotes.length,
            avgVelocity: velocities.reduce((a, b) => a + b, 0) / velocities.length,
            minVelocity: Math.min(...velocities),
            maxVelocity: Math.max(...velocities),
            avgDuration: durations.reduce((a, b) => a + b, 0) / durations.length,
            avgInterval: fingerIntervals.length > 0 ? 
              (fingerIntervals.reduce((a, b) => a + b, 0) / fingerIntervals.length) * 1000 : 0, // in ms
            percentage: (fingerNotes.length / notes.length * 100).toFixed(1)
          };
        } else {
          fingerStats[hand][finger] = {
            count: 0,
            avgVelocity: 0,
            minVelocity: 0,
            maxVelocity: 0,
            avgDuration: 0,
            avgInterval: 0,
            percentage: 0
          };
        }
      });
    });
    
    setRecordAnalysis({
      totalNotes: notes.length,
      leftHandNotes: leftNotes.length,
      rightHandNotes: rightNotes.length,
      tempo: overallBPM,
      tempoLeft: leftBPM,
      tempoRight: rightBPM,
      avgInterval: avgLeftInterval * 1000 || avgRightInterval * 1000, // Use whichever hand has data
      avgIntervalLeft: avgLeftInterval * 1000,
      avgIntervalRight: avgRightInterval * 1000,
      avgVelocity: avgVelocity,
      minVelocity: minVelocity,
      maxVelocity: maxVelocity,
      avgDuration: avgDuration,
      minPitch: minPitch,
      maxPitch: maxPitch,
      pitchRange: maxPitch - minPitch,
      leftHandAvgVelocity: leftVelocity,
      rightHandAvgVelocity: rightVelocity,
      totalDuration: notes.length > 0 ? 
        (notes[notes.length - 1].timestamp - notes[0].timestamp) : 0,
      fingerStats: fingerStats
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>

      {adminLoadError && (
        <div
          role="alert"
          className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800"
        >
          {adminLoadError}
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 border-b">
        <button
          onClick={() => setActiveTab('record')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'record' 
              ? 'border-b-2 border-indigo-600 text-indigo-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <PlayIcon className="h-5 w-5 inline mr-2" />
          Record & Classify
        </button>
        <button
          onClick={() => setActiveTab('data')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'data' 
              ? 'border-b-2 border-indigo-600 text-indigo-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <DocumentTextIcon className="h-5 w-5 inline mr-2" />
          Training Data
        </button>
        <button
          onClick={() => setActiveTab('model')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'model' 
              ? 'border-b-2 border-indigo-600 text-indigo-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <BeakerIcon className="h-5 w-5 inline mr-2" />
          Model Training
        </button>
        <button
          onClick={() => setActiveTab('exercises')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'exercises' 
              ? 'border-b-2 border-indigo-600 text-indigo-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CubeIcon className="h-5 w-5 inline mr-2" />
          Exercises
        </button>
        <button
          onClick={() => setActiveTab('embeddings')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'embeddings' 
              ? 'border-b-2 border-indigo-600 text-indigo-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <TableCellsIcon className="h-5 w-5 inline mr-2" />
          Embeddings
        </button>
        <button
          onClick={() => setActiveTab('recognition')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'recognition'
              ? 'border-b-2 border-indigo-600 text-indigo-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <AdjustmentsHorizontalIcon className="h-5 w-5 inline mr-2" />
          Recognition
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'recognition' && (
        <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl">
          <h2 className="text-xl font-semibold">Exercise Recognition Gate</h2>
          <p className="mt-2 text-sm text-gray-600">
            Every enabled condition must pass before Practice Meter generates
            exercise-specific analysis or saves a session. Raise thresholds to
            reject more uncertain recordings.
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                key: 'min_similarity',
                label: 'Minimum similarity',
                step: 0.001,
                min: 0,
                max: 1,
                help: 'Absolute cosine similarity of the strongest embedding.'
              },
              {
                key: 'min_margin',
                label: 'Minimum top-two margin',
                step: 0.001,
                min: 0,
                max: 1,
                help: 'Required separation between the best and runner-up match.'
              },
              {
                key: 'min_pitch_agreement',
                label: 'Minimum canonical pitch agreement',
                step: 0.01,
                min: 0,
                max: 1,
                help: 'Share of played notes contained in the exercise pitch set.'
              },
              {
                key: 'min_notes',
                label: 'Minimum notes per usable hand',
                step: 1,
                min: 5,
                max: 200,
                help: 'Evidence required before one hand can support recognition.'
              }
            ].map((field) => (
              <label key={field.key} className="block">
                <span className="text-sm font-medium text-gray-800">
                  {field.label}
                </span>
                <input
                  type="number"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={recognitionThresholds[field.key]}
                  onChange={(event) =>
                    setRecognitionThresholds((current) => ({
                      ...current,
                      [field.key]:
                        field.key === 'min_notes'
                          ? Number.parseInt(event.target.value, 10)
                          : Number.parseFloat(event.target.value)
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <span className="mt-1 block text-xs text-gray-500">
                  {field.help}
                </span>
              </label>
            ))}
          </div>

          <label className="mt-6 flex items-start gap-3">
            <input
              type="checkbox"
              checked={recognitionThresholds.require_hand_agreement}
              onChange={(event) =>
                setRecognitionThresholds((current) => ({
                  ...current,
                  require_hand_agreement: event.target.checked
                }))
              }
              className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              <span className="block text-sm font-medium text-gray-800">
                Require agreement between hands
              </span>
              <span className="block text-xs text-gray-500">
                When both hands contain enough notes, reject the take unless
                they independently identify the same exercise.
              </span>
            </span>
          </label>

          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={saveRecognitionThresholds}
              disabled={savingRecognitionSettings}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingRecognitionSettings ? 'Saving…' : 'Save thresholds'}
            </button>
            {recognitionSettingsStatus && (
              <span className="text-sm text-gray-600">
                {recognitionSettingsStatus}
              </span>
            )}
          </div>
        </div>
      )}

      {activeTab === 'record' && (
        <div className="space-y-6">
          {/* Recording Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Recording Session</h2>
            
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isRecording ? (
                  <>
                    <StopIcon className="h-5 w-5" />
                    Stop Recording
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-5 w-5" />
                    Start Recording
                  </>
                )}
              </button>
              
              {(completedNotes.length > 0 || activeNotes.length > 0) && (
                <>
                  <span className="text-gray-600">
                    {completedNotes.length} completed, {activeNotes.length} active
                  </span>
                  <button
                    onClick={() => {
                      console.log('AdminPanel: Clearing notes');
                      setCompletedNotes([]);
                      setActiveNotes([]);
                    }}
                    className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded"
                  >
                    Clear Notes
                  </button>
                </>
              )}
            </div>

            {/* MIDI Roll Display */}
            {(completedNotes.length > 0 || activeNotes.length > 0) && (
              <div className="mb-4">
                <h3 className="text-lg font-medium mb-2">MIDI Roll (Click notes to toggle hand classification)</h3>
                <div className="h-96 border rounded-lg overflow-hidden relative">
                  <CustomMidiRoll 
                    notes={completedNotes} 
                    activeNotes={activeNotes}
                    isRecording={isRecording}
                    analysisResults={null}
                  />
                  {/* Overlay for click handling */}
                  <div 
                    className="absolute inset-0 left-16" 
                    style={{ pointerEvents: isRecording ? 'none' : 'auto' }}
                    onClick={(e) => {
                      if (isRecording) return;
                      
                      // Calculate which note was clicked based on position
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      const relativeX = x / rect.width;
                      const relativeY = y / rect.height;
                      
                      // Find the note at this position
                      const clickedNote = completedNotes.find(note => {
                        // Calculate note's vertical position (21 = A0, 108 = C8)
                        const noteRange = 108 - 21 + 1;
                        const noteIndex = note.note - 21;
                        const noteYPos = ((noteRange - noteIndex - 1) / noteRange);
                        const noteHeight = 1 / noteRange;
                        
                        // Check if click is within note's vertical range
                        if (relativeY >= noteYPos && relativeY <= noteYPos + noteHeight) {
                          // For simplicity, any horizontal click on the note counts
                          return true;
                        }
                        return false;
                      });
                      
                      if (clickedNote) {
                        toggleNoteHand(clickedNote);
                      }
                    }}
                  />
                </div>
                <div className="mt-2 flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span>Left Hand</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span>Right Hand</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-400 rounded"></div>
                    <span>Unclassified</span>
                  </div>
                </div>
              </div>
            )}

            {/* Save Recording */}
            {completedNotes.length > 0 && (
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  placeholder="Enter file name (e.g., scale_c_maj)"
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={saveRecording}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Save Recording & Generate Embedding
                </button>
              </div>
            )}
          </div>

          {/* Detailed Note Table */}
          {completedNotes.length > 0 && !isRecording && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Note-by-Note Analysis</h2>
              
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pitch</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Time (s)</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Time (s)</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Duration (ms)</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Velocity</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hand</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Interval (ms)</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {completedNotes.map((note, index) => {
                      const endTime = note.timestamp + (note.duration || 0) / 1000;
                      const interval = index > 0 ? 
                        (note.timestamp - completedNotes[index - 1].timestamp) * 1000 : 0;
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-900">{index + 1}</td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900">{note.note}</td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900">
                            {getNoteNameFromMIDI(note.note)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {note.timestamp.toFixed(3)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {endTime.toFixed(3)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {(note.duration || 0).toFixed(0)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">{note.velocity}</td>
                          <td className="px-3 py-2 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              note.hand === 'left' 
                                ? 'bg-blue-100 text-blue-800' 
                                : note.hand === 'right'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {note.hand || 'unclassified'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {note.confidence ? `${(note.confidence * 100).toFixed(1)}%` : '-'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {index > 0 ? interval.toFixed(0) : '-'}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => toggleNoteHand(note)}
                              className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                            >
                              Toggle
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Comprehensive Analysis Table */}
          {recordAnalysis && !isRecording && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Exercise Data Analysis</h2>
              
              <div className="grid grid-cols-2 gap-6">
                {/* General Statistics */}
                <div>
                  <h3 className="text-lg font-medium mb-3 text-gray-800">General Statistics</h3>
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Total Notes:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">{recordAnalysis.totalNotes}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Total Duration:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.totalDuration.toFixed(2)} seconds
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Tempo (BPM) - Overall:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">{recordAnalysis.tempo}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Tempo (BPM) - Left Hand:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">{recordAnalysis.tempoLeft || '-'}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Tempo (BPM) - Right Hand:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">{recordAnalysis.tempoRight || '-'}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Avg Interval - Left:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.avgIntervalLeft ? `${recordAnalysis.avgIntervalLeft.toFixed(0)} ms` : '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Avg Interval - Right:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.avgIntervalRight ? `${recordAnalysis.avgIntervalRight.toFixed(0)} ms` : '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Pitch Range:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {getNoteNameFromMIDI(recordAnalysis.minPitch)} - {getNoteNameFromMIDI(recordAnalysis.maxPitch)}
                          <span className="text-gray-500 ml-1">({recordAnalysis.pitchRange} semitones)</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Velocity Statistics */}
                <div>
                  <h3 className="text-lg font-medium mb-3 text-gray-800">Velocity Analysis</h3>
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Average Velocity:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.avgVelocity.toFixed(1)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Min Velocity:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">{recordAnalysis.minVelocity}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Max Velocity:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">{recordAnalysis.maxVelocity}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Avg Duration:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.avgDuration.toFixed(0)} ms
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Hand-Specific Statistics */}
                <div>
                  <h3 className="text-lg font-medium mb-3 text-gray-800">Left Hand Statistics</h3>
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Notes Played:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.leftHandNotes}
                          <span className="text-gray-500 ml-1">
                            ({((recordAnalysis.leftHandNotes / recordAnalysis.totalNotes) * 100).toFixed(1)}%)
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Avg Velocity:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.leftHandAvgVelocity.toFixed(1)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-3 text-gray-800">Right Hand Statistics</h3>
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Notes Played:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.rightHandNotes}
                          <span className="text-gray-500 ml-1">
                            ({((recordAnalysis.rightHandNotes / recordAnalysis.totalNotes) * 100).toFixed(1)}%)
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 text-sm font-medium text-gray-600">Avg Velocity:</td>
                        <td className="py-2 text-sm text-gray-900 text-right">
                          {recordAnalysis.rightHandAvgVelocity.toFixed(1)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Finger-by-Finger Analysis */}
              {recordAnalysis.fingerStats && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-3 text-gray-800">Left Hand - Finger Analysis</h3>
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Finger</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">%</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Vel</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Int (ms)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {[1, 2, 3, 4, 5].map(finger => {
                          const stats = recordAnalysis.fingerStats.left[finger];
                          return stats && stats.count > 0 ? (
                            <tr key={finger}>
                              <td className="px-3 py-2 text-sm font-medium text-gray-900">L{finger}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.count}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.percentage}%</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.avgVelocity.toFixed(0)}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.avgInterval.toFixed(0)}</td>
                            </tr>
                          ) : null;
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-3 text-gray-800">Right Hand - Finger Analysis</h3>
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Finger</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">%</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Vel</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Int (ms)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {[1, 2, 3, 4, 5].map(finger => {
                          const stats = recordAnalysis.fingerStats.right[finger];
                          return stats && stats.count > 0 ? (
                            <tr key={finger}>
                              <td className="px-3 py-2 text-sm font-medium text-gray-900">R{finger}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.count}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.percentage}%</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.avgVelocity.toFixed(0)}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{stats.avgInterval.toFixed(0)}</td>
                            </tr>
                          ) : null;
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Data to be Saved */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium mb-3 text-gray-800">Data Structure for Saving</h3>
                <pre className="text-xs bg-gray-800 text-green-400 p-4 rounded overflow-x-auto">
{JSON.stringify({
  exercise_metadata: {
    total_notes: recordAnalysis.totalNotes,
    duration_seconds: recordAnalysis.totalDuration,
    tempo_npm: recordAnalysis.tempo,
    pitch_range: {
      min: recordAnalysis.minPitch,
      max: recordAnalysis.maxPitch,
      range_semitones: recordAnalysis.pitchRange
    }
  },
  velocity_stats: {
    average: recordAnalysis.avgVelocity,
    min: recordAnalysis.minVelocity,
    max: recordAnalysis.maxVelocity
  },
  timing_stats: {
    avg_interval_ms: recordAnalysis.avgInterval,
    avg_duration_ms: recordAnalysis.avgDuration
  },
  hand_distribution: {
    left: {
      note_count: recordAnalysis.leftHandNotes,
      percentage: ((recordAnalysis.leftHandNotes / recordAnalysis.totalNotes) * 100).toFixed(1),
      avg_velocity: recordAnalysis.leftHandAvgVelocity
    },
    right: {
      note_count: recordAnalysis.rightHandNotes,
      percentage: ((recordAnalysis.rightHandNotes / recordAnalysis.totalNotes) * 100).toFixed(1),
      avg_velocity: recordAnalysis.rightHandAvgVelocity
    }
  },
  notes: completedNotes.map(n => ({
    note: n.note,
    velocity: n.velocity,
    timestamp: n.timestamp,
    duration: n.duration,
    hand: n.hand
  }))
}, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'data' && (
        <div className="space-y-6">
          {/* CSV Files List */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Training Data Files</h2>
              <button
                onClick={fetchCsvFiles}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowPathIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-4 mb-2">
                <span className="text-sm text-gray-600">
                  {selectedCsvFiles.size} of {csvFiles.length} files selected
                </span>
                {selectedCsvFiles.size > 0 && (
                  <button
                    onClick={() => setSelectedCsvFiles(new Set())}
                    className="text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    Clear selection
                  </button>
                )}
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      <input
                        type="checkbox"
                        checked={selectedCsvFiles.size === csvFiles.length && csvFiles.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCsvFiles(new Set(csvFiles.map(f => f.name)));
                          } else {
                            setSelectedCsvFiles(new Set());
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Modified</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {csvFiles.map((file, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedCsvFiles.has(file.name)}
                          onChange={() => toggleCsvSelection(file.name)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm font-mono">{file.name}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{file.size}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{file.note_count || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{file.modified || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'model' && (
        <div className="space-y-6">
          {/* Model Configuration */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Model Configuration</h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hidden Layers
                </label>
                <input
                  type="text"
                  value={modelConfig.hidden_layers.join(', ')}
                  onChange={(e) => {
                    const layers = e.target.value.split(',').map(l => parseInt(l.trim())).filter(n => !isNaN(n));
                    setModelConfig(prev => ({ ...prev, hidden_layers: layers }));
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., 128, 64, 32"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated layer sizes</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Epochs
                </label>
                <input
                  type="number"
                  value={modelConfig.epochs}
                  onChange={(e) => setModelConfig(prev => ({ ...prev, epochs: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="1"
                  max="1000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Learning Rate
                </label>
                <input
                  type="number"
                  value={modelConfig.learning_rate}
                  onChange={(e) => setModelConfig(prev => ({ ...prev, learning_rate: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  step="0.0001"
                  min="0.0001"
                  max="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Batch Size
                </label>
                <input
                  type="number"
                  value={modelConfig.batch_size}
                  onChange={(e) => setModelConfig(prev => ({ ...prev, batch_size: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="1"
                  max="256"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sequence Length
                </label>
                <input
                  type="number"
                  value={modelConfig.sequence_length}
                  onChange={(e) => setModelConfig(prev => ({ ...prev, sequence_length: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="4"
                  max="64"
                />
                <p className="text-xs text-gray-500 mt-1">Number of notes per training sequence</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Validation Split
                </label>
                <input
                  type="number"
                  value={modelConfig.validation_split}
                  onChange={(e) => setModelConfig(prev => ({ ...prev, validation_split: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  step="0.05"
                  min="0.05"
                  max="0.5"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Classes
                </label>
                <select
                  value={modelConfig.num_classes}
                  onChange={(e) => setModelConfig(prev => ({ ...prev, num_classes: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={2}>2 (Binary: left/right)</option>
                  <option value={3}>3 (Multi-class: left/right/single-hand)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {modelConfig.num_classes === 3 
                    ? 'Includes single-hand detection. Make sure to include _2 and _3 labeled CSV files in training data.'
                    : 'Traditional binary classification for two-handed playing only.'}
                </p>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="use_dropout"
                  checked={modelConfig.use_dropout}
                  onChange={(e) => setModelConfig(prev => ({ ...prev, use_dropout: e.target.checked }))}
                  className="rounded border-gray-300 mr-2"
                />
                <label htmlFor="use_dropout" className="text-sm font-medium text-gray-700">
                  Use Dropout
                </label>
                {modelConfig.use_dropout && (
                  <input
                    type="number"
                    value={modelConfig.dropout_rate}
                    onChange={(e) => setModelConfig(prev => ({ ...prev, dropout_rate: parseFloat(e.target.value) }))}
                    className="ml-4 px-2 py-1 border rounded text-sm"
                    step="0.05"
                    min="0"
                    max="0.9"
                    placeholder="Rate"
                  />
                )}
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Input Layer:</strong> {modelConfig.sequence_length * 2} features ({modelConfig.sequence_length} notes × 2 features: normalized note + time delta)
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Each note has 2 features:</strong>
              </p>
              <ul className="text-xs text-gray-500 ml-4 mb-2 space-y-1">
                <li>• Normalized note number (MIDI note / 127, range 0-1)</li>
                <li>• Time since last note (in seconds)</li>
              </ul>
              <p className="text-sm text-gray-600">
                <strong>Output Layer:</strong> {modelConfig.num_classes === 2 
                  ? 'Binary classification (left/right hand)'
                  : '3-class classification (left/right/single-hand)'}
              </p>
            </div>
            
            <button
              onClick={trainModel}
              disabled={isTraining}
              className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BeakerIcon className="h-5 w-5" />
              {isTraining ? 'Training Model...' : 'Train New Model'}
            </button>
            
            {trainingResult && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-medium text-green-800 mb-2">Training Complete!</h3>
                <div className="grid grid-cols-2 gap-4 text-sm text-green-700">
                  <div>
                    <span className="font-medium">Model:</span> {trainingResult.model_filename}
                  </div>
                  <div>
                    <span className="font-medium">Validation Accuracy:</span> {(trainingResult.val_accuracy * 100).toFixed(2)}%
                  </div>
                  <div>
                    <span className="font-medium">Training Accuracy:</span> {(trainingResult.train_accuracy * 100).toFixed(2)}%
                  </div>
                  <div>
                    <span className="font-medium">Training Samples:</span> {trainingResult.train_samples}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Trained Models List */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Trained Models</h2>
            
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {models.map((model, index) => {
                    const numClasses = model.num_classes || 2;
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          {model.is_active ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircleIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm font-mono">{model.name}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            numClasses === 3 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {numClasses === 3 ? '3-class' : '2-class'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {model.training_metrics?.val_accuracy 
                            ? `${(model.training_metrics.val_accuracy * 100).toFixed(2)}%`
                            : '-'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">{model.created_at || '-'}</td>
                        <td className="px-4 py-2">
                          {!model.is_active && (
                            <button
                              onClick={() => activateModel(model.name)}
                              className="text-sm text-indigo-600 hover:text-indigo-700"
                            >
                              Activate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'exercises' && (
        <div className="space-y-6">
          {/* Exercises List */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Available Exercises</h2>
              <button
                onClick={fetchExercises}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowPathIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Display Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">In DB</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Has Embedding</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {exercises.map((exercise, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-mono">{exercise.name}</td>
                      <td className="px-4 py-2 text-sm">{exercise.display_name}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{exercise.type}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{exercise.difficulty_level}</td>
                      <td className="px-4 py-2">
                        {exercise.in_database ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {exercise.has_embedding ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'embeddings' && (
        <div className="space-y-6">
          {/* Embeddings Grid */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Exercise Embeddings</h2>
              <button
                onClick={fetchEmbeddings}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowPathIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            <div className="text-sm text-gray-600 mb-4">
              Showing {embeddings.length} exercise embeddings (62 features each)
            </div>
            
            <div className="overflow-x-auto">
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-10">
                        Exercise
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" colSpan="12">
                        Pitch Freq (12)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" colSpan="2">
                        Hand Freq (2)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" colSpan="24">
                        1-Note Intervals (24)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" colSpan="24">
                        2-Note Intervals (24)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {embeddings.map((embedding, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-mono sticky left-0 bg-white z-10">
                          {embedding.exercise_name}
                        </td>
                        {embedding.features.map((feature, fIdx) => (
                          <td key={fIdx} className="px-2 py-1 text-xs text-center">
                            <div 
                              className={`rounded px-1 ${
                                feature > 0.5 ? 'bg-green-100 text-green-800' :
                                feature > 0.2 ? 'bg-yellow-100 text-yellow-800' :
                                feature > 0 ? 'bg-gray-100 text-gray-600' :
                                'bg-gray-50 text-gray-400'
                              }`}
                              title={`Feature ${fIdx}: ${feature.toFixed(3)}`}
                            >
                              {feature.toFixed(2)}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
              <p><strong>Feature Groups:</strong></p>
              <ul className="mt-2 space-y-1">
                <li>• Features 0-11: Pitch frequency distribution</li>
                <li>• Features 12-13: Hand frequency (left/right)</li>
                <li>• Features 14-37: 1-note intervals with direction</li>
                <li>• Features 38-61: 2-note intervals with direction</li>
              </ul>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPanel;
